import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import { Hash, Menu, QrCode } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import PreferencesPanel from '../../components/PreferencesPanel';

const DX_LOGO_URL = 'https://cdn.art-mate.net/uploads/artmate/202502/thumbnail/20250228_160227_1YqJgAgCUS_p_300_0.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeEntry = () => {
  const { language, fontSize, t, setAccessMethod } = useLanguage();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Font size classes based on global preference
  const getFontSizeClass = () => {
    if (fontSize === 'small') return 'text-sm';
    if (fontSize === 'large') return 'text-xl';
    return 'text-base';
  };

  // Auto-submit when 3 digits entered
  useEffect(() => {
    if (code.length === 3) {
      handleSubmit();
    }
  }, [code]);

  const handleSubmit = async () => {
    if (code.length !== 3) return;

    setLoading(true);
    setAccessMethod('code');
    try {
      // Try zone first
      try {
        const zoneResponse = await axios.get(`${API}/zones/by-code/${code}`);
        const zone = zoneResponse.data;
        navigate(`/zone/${zone.id}`);
        return;
      } catch (zoneError) {
        // If zone not found, try exhibit
        if (zoneError.response?.status === 404) {
          const exhibitResponse = await axios.get(`${API}/exhibits/by-code/${code}`);
          const exhibit = exhibitResponse.data;
          navigate(`/exhibit/${exhibit.id}`);
          return;
        }
        throw zoneError;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t({ 
          en: 'Code not found. Please check the code.', 
          tc: '找不到代碼，請檢查代碼', 
          sc: '找不到代码，请检查代码' 
        }));
      } else {
        toast.error(t({ 
          en: 'Error accessing content', 
          tc: '無法存取內容', 
          sc: '无法访问内容' 
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
    setCode(value);
  };

  return (
    <div className="min-h-screen flex flex-col bg-black" data-testid="code-entry-page">
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between relative">
          <img 
            src={DX_LOGO_URL} 
            alt="DX Logo" 
            className="w-28 object-contain"
          />
          {/* <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({ en: 'Enter Code', tc: '輸入代碼', sc: '输入代码' })}
          </h1> */}
          <button
            onClick={() => setPreferencesOpen(true)}
            className="text-white hover:bg-white/10 rounded-lg p-2"
            data-testid="open-preferences"
          >
            <Menu className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="self-start">
          <h1 className="absolute left-1/2 -translate-x-1/2 text-2xl font-heading font-bold text-white">
            {t({ en: 'Enter Code', tc: '輸入代碼', sc: '输入代码' })}
          </h1> 
        </div>         
        <div className="w-full max-w-md space-y-8">
          {/* Icon */}
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
              <Hash className="w-10 h-10 text-white" />
            </div>
            <p className={`text-center text-white/70 ${getFontSizeClass()}`}>
              {t({ 
                en: 'Enter the 3-digit code displayed near the exhibit', 
                tc: '輸入展品旁顯示的3位數字代碼', 
                sc: '输入展品旁显示的3位数字代码' 
              })}
            </p>
          </div>

          {/* Code Input */}
          <div className="space-y-6">
            <div className="flex justify-center">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={handleCodeChange}
                placeholder="000"
                className="w-48 h-24 text-6xl text-center font-mono tracking-widest bg-white/10 border-white/20 text-white placeholder:text-white/30"
                maxLength={3}
                autoFocus
                data-testid="code-input"
              />
            </div>

            {loading && (
              <p className={`text-center text-white/60 ${getFontSizeClass()}`}>
                {t({ en: 'Loading...', tc: '載入中...', sc: '加载中...' })}
              </p>
            )}
          </div>

          {/* Help Text */}
          <div className="text-center space-y-4">
            <p className={`text-white/50 ${getFontSizeClass()}`}>
              {t({ 
                en: 'Code example: 123, 045, 789', 
                tc: '代碼範例：123、045、789', 
                sc: '代码示例：123、045、789' 
              })}
            </p>
            
            {/* Scan QR Code Link with Icon */}
            <button
              onClick={() => {
                setAccessMethod('qr');
                navigate('/qr-instructions');
              }}
              className="flex items-center justify-center gap-3 mx-auto px-6 py-3 rounded-xl bg-white/5 border border-white/20 hover:bg-white/10 transition-all"
              data-testid="scan-qr-instead"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <QrCode className="w-7 h-7 text-white" />
              </div>
              <span className={`text-white font-medium ${getFontSizeClass()}`}>
                {t({ 
                  en: 'Scan QR code instead', 
                  tc: '改用掃描二維碼', 
                  sc: '改用扫描二维码' 
                })}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Preferences Panel */}
      <PreferencesPanel 
        isOpen={preferencesOpen} 
        onClose={() => setPreferencesOpen(false)} 
      />
    </div>
  );
};

export default CodeEntry;
